import { dayjs } from '~/plugins/dayjs';

export const NAV_NEXT_SOURCE = 'nav-next';

export const DEFAULT_FACTORY_SETTINGS = {
  firstDayOfWeek: 'MONDAY',
  startProductionTime: '00:00:00',
  startProductionDayOffset: 0,
  timezone: 'UTC',
};

// Single owner of the weekday mapping — re-exported by the modal contract,
// imported by navShortcuts (week-start arithmetic must never diverge).
export const WEEKDAY_INDEXES = {
  SUNDAY: 0,
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
};

export function weekStartIndex(settings) {
  return WEEKDAY_INDEXES[settings.firstDayOfWeek] ?? WEEKDAY_INDEXES.MONDAY;
}

/**
 * Degraded-mode mapping (S-0020, amendé S-0024): the production-day settings
 * do not exist — civil defaults, only the timezone survives. Single encoding,
 * shared by the resolution and the modal context mappings.
 */
export function degradeSettings(settings) {
  return { ...DEFAULT_FACTORY_SETTINGS, timezone: settings.timezone };
}

/**
 * Keep only the factory-settings fields of a mixed object ({ id, name, ... }
 * shapes travel through the modal context) — identity fields must never leak
 * into the effective settings.
 */
export function pickFactorySettings(source) {
  return Object.fromEntries(
    Object.entries(source ?? {}).filter(([key]) => key in DEFAULT_FACTORY_SETTINGS),
  );
}

/**
 * Null-safe ingestion of a GraphQL factory: without useAvailabilityPeriods the
 * production-day fields were never configured (nullable in DB) — normalize to
 * the civil defaults so every downstream reader can trust the shape (S-0020).
 * Single encoding, shared by the settings feed and the reference-factory query.
 */
export function normalizeFactorySettings(factory) {
  return {
    firstDayOfWeek: factory.firstDayOfWeek ?? DEFAULT_FACTORY_SETTINGS.firstDayOfWeek,
    startProductionTime: factory.startProductionTime
      ?? DEFAULT_FACTORY_SETTINGS.startProductionTime,
    startProductionDayOffset: factory.startProductionDayOffset
      ?? DEFAULT_FACTORY_SETTINGS.startProductionDayOffset,
    timezone: factory.timezone,
  };
}

/**
 * Two resolved periods are equivalent when their six scalar fields match —
 * lets the single writer skip a store commit that would invalidate every
 * reader (widget queries, boundaries readers) for an identical window.
 */
export function sameResolvedPeriod(left, right) {
  if (!left || !right) {
    return left === right;
  }

  return ['start', 'end', 'startFullDay', 'endFullDay', 'startWindow', 'endWindow', 'firstDayLabel', 'timezone', 'source']
    .every((field) => left[field] === right[field]);
}

const UTC_NAIVE_FORMAT = 'YYYY-MM-DD HH:mm:ss';

function toUtcNaive(zoned) {
  return zoned.utc().format(UTC_NAIVE_FORMAT);
}

/**
 * @param {Object} options
 * @param {boolean} options.withoutOrder - read the window BEFORE the OF step (period × time
 *   range), for the modules that must not be narrowed by the selected OF; the legacy
 *   navigation never narrows by OF, so this keeps both navigations alike (KP-22336)
 */
export function resolvedPeriodBoundaries(resolvedPeriod, {
  limitToNow = false,
  forceFullDay = false,
  withoutOrder = false,
  now = dayjs.utc(),
} = {}) {
  if (resolvedPeriod?.source !== NAV_NEXT_SOURCE) {
    return null;
  }

  let startValue = resolvedPeriod.start;
  let endValue = resolvedPeriod.end;
  if (forceFullDay) {
    startValue = resolvedPeriod.startFullDay;
    endValue = resolvedPeriod.endFullDay;
  } else if (withoutOrder && resolvedPeriod.startWindow && resolvedPeriod.endWindow) {
    startValue = resolvedPeriod.startWindow;
    endValue = resolvedPeriod.endWindow;
  }
  if (!startValue || !endValue) {
    return null;
  }

  const nowUtc = dayjs.utc(now);
  const start = dayjs.utc(startValue);
  let end = dayjs.utc(endValue);
  // Clamp only a window that has STARTED: on a window entirely ahead of now
  // (« next week » on a grid television) clamping would return end < start —
  // an impossible range that the backend refuses (`after:start` rules).
  if (limitToNow && nowUtc.isBefore(end) && nowUtc.isAfter(start)) {
    end = nowUtc;
  }

  return {
    start: toUtcNaive(start),
    end: toUtcNaive(end),
  };
}

/**
 * Anchor a calendar date on the production-day start in the factory timezone.
 * dayjs.tz on the composed string keeps DST-shifted days (23h/25h) exact.
 */
function productionDayStart(dateString, settings) {
  return dayjs.tz(`${dateString} ${settings.startProductionTime}`, settings.timezone);
}

/**
 * Shift a zoned instant by whole days on the WALL CLOCK of `timezone`. dayjs-timezone's
 * add/subtract applies the BROWSER's DST: a New York site seen from Paris on
 * 25/10 got a 25h day (08:00 -> 09:00).
 */
export function addZonedDays(zoned, days, timezone) {
  const wallClock = dayjs.utc(zoned.tz(timezone).format(UTC_NAIVE_FORMAT)).add(days, 'day');

  return dayjs.tz(wallClock.format(UTC_NAIVE_FORMAT), timezone);
}

/**
 * Reference factory of the resolution (S-0024, remplace R4/S-0021) :
 * - the scoped selection maps to a SINGLE factory → that factory;
 * - several factories → the tenant DEFAULT factory, even outside the
 *   selection (tenant parameter: stable window whatever the filtering);
 * - no flagged default (inconsistent data) → deterministic silent TIE-BREAK
 *   on the smallest id of the displayed scope. This is not an election: the
 *   backend owns the real « oldest » rule (created_at — Factory model) and
 *   guarantees a default normally always exists;
 * - empty scope → the tenant default when known, browser guess otherwise
 *   (transient, before the entity picker feeds the settings).
 *
 * @param {Object} factorySettings map id → settings (scoped selection)
 * @param {Object|null} defaultFactorySettings flat `{ id, name, ...settings }`
 * @returns {{id: string|null, name: string|null, settings: Object|null,
 *   source: 'single'|'default'|'fallback'|'none'}}
 */
export function electReferenceFactory(factorySettings, defaultFactorySettings = null) {
  const entries = Object.entries(factorySettings ?? {});

  if (entries.length === 1) {
    const [id, settings] = entries[0];

    return {
      id: String(id), name: null, settings, source: 'single',
    };
  }
  if (defaultFactorySettings) {
    const { id, name = null, ...settings } = defaultFactorySettings;

    return {
      id: String(id), name, settings, source: 'default',
    };
  }
  if (entries.length > 1) {
    const [id, settings] = entries
      .reduce((oldest, entry) => (Number(oldest[0]) <= Number(entry[0]) ? oldest : entry));

    return {
      id: String(id), name: null, settings, source: 'fallback',
    };
  }

  return {
    id: null, name: null, settings: null, source: 'none',
  };
}

/**
 * Effective settings for the resolution: the REFERENCE factory dictates the
 * timezone and the production-day settings (S-0024 — fin du jour civil forcé
 * R4 et de la timezone navigateur en multi-sites).
 *
 * Degraded mode (S-0020, amendé S-0024): the production-day settings do not
 * exist, the day is civil everywhere — in the timezone of the REFERENCE
 * factory (never the browser's).
 */
export function resolveEffectiveSettings(
  factorySettings,
  { degradedMode = false, defaultFactorySettings = null } = {},
) {
  const reference = electReferenceFactory(factorySettings, defaultFactorySettings);
  const effective = reference.settings
    ? { ...DEFAULT_FACTORY_SETTINGS, ...reference.settings }
    : { ...DEFAULT_FACTORY_SETTINGS, timezone: dayjs.tz.guess() };

  if (degradedMode) {
    return degradeSettings(effective);
  }

  return effective;
}

/**
 * [start, end) of the production day labeled `dateString`, shifted by the
 * reference-day offset (J = 0, J-1 = 1 : the day labeled D starts on D-offset).
 */
function productionDayBounds(dateString, settings) {
  const labelStart = addZonedDays(
    productionDayStart(dateString, settings),
    -settings.startProductionDayOffset,
    settings.timezone,
  );

  return { start: labelStart, end: addZonedDays(labelStart, 1, settings.timezone) };
}

function weekBounds(dateStrings, settings) {
  const reference = Array.isArray(dateStrings) ? dateStrings[0] : dateStrings;
  const refStart = productionDayBounds(reference, settings).start;
  const targetIndex = weekStartIndex(settings);
  // Week alignment happens in LABEL space (same space as weekLabelsContaining
  // and the picker's periodValue), never on the start instant's weekday: with
  // J-1 the instant of a Monday-labeled day falls on a Sunday, and aligning on
  // it slipped the window back a whole week (picker « 10-16/08 » resolved to
  // 03-10/08).
  const labelWeekday = dayjs(reference).day();
  const backDays = (labelWeekday - targetIndex + 7) % 7;
  const start = addZonedDays(refStart, -backDays, settings.timezone);

  return { start, end: addZonedDays(start, 7, settings.timezone) };
}

function monthBounds(monthString, settings) {
  const first = dayjs(`${monthString}-01`);
  const { start } = productionDayBounds(first.format('YYYY-MM-DD'), settings);
  const nextMonthFirst = first.add(1, 'month').format('YYYY-MM-DD');

  return { start, end: productionDayBounds(nextMonthFirst, settings).start };
}

function yearBounds(yearString, settings) {
  const { start } = productionDayBounds(`${yearString}-01-01`, settings);

  return { start, end: productionDayBounds(`${Number(yearString) + 1}-01-01`, settings).start };
}

function customBounds(range, settings) {
  const [from, to] = range;
  const { start } = productionDayBounds(from, settings);
  const { end } = productionDayBounds(to, settings);

  return { start, end };
}

function periodBounds(periodType, periodValue, settings) {
  switch (periodType) {
    case 'now':
    case 'Day':
      return productionDayBounds(periodValue, settings);
    case 'Week':
      return weekBounds(periodValue, settings);
    case 'Month':
      return monthBounds(periodValue, settings);
    case 'Year':
      return yearBounds(periodValue, settings);
    case 'Custom':
      return customBounds(periodValue, settings);
    default:
      return null;
  }
}

/**
 * Custom hours override the factory anchoring as GLOBAL bounds: start time on
 * the first day, end time on the last day; end <= start rolls to the next day
 * (valid night range). Days are the CIVIL labels of the resolved period.
 */
function applyCustomHours(bounds, customHours, settings) {
  const firstDay = bounds.start.format('YYYY-MM-DD');
  const lastDay = addZonedDays(bounds.end, -1, settings.timezone).format('YYYY-MM-DD');
  const start = dayjs.tz(`${firstDay} ${customHours.startTime}`, settings.timezone);
  let end = dayjs.tz(`${lastDay} ${customHours.endTime}`, settings.timezone);
  // Night range: comparing TIMES (not instants) so multi-day periods roll too
  // (week 22:00 -> 06:00 ends the day after the last day, §2bis).
  if (customHours.endTime <= customHours.startTime) {
    end = addZonedDays(end, 1, settings.timezone);
  }

  return { start, end };
}

/**
 * Day-mode time range selection: FULL_DAY = civil day 00:00 -> next 00:00 in
 * the factory timezone; SHIFT = explicit periods (already zoned instants).
 */
function applyTimeRange(bounds, timeRange, periodType, periodValue, settings) {
  if (periodType !== 'Day' || !timeRange || timeRange.kind === 'PRODUCTION_DAY') {
    return bounds;
  }
  if (timeRange.kind === 'FULL_DAY') {
    const start = dayjs.tz(`${periodValue} 00:00:00`, settings.timezone);

    return { start, end: addZonedDays(start, 1, settings.timezone) };
  }
  if (timeRange.kind === 'SHIFT' && timeRange.periods?.length) {
    const starts = timeRange.periods.map((p) => dayjs(p.start));
    const ends = timeRange.periods.map((p) => dayjs(p.end));

    return {
      start: starts.reduce((a, b) => (a.isBefore(b) ? a : b)),
      end: ends.reduce((a, b) => (a.isAfter(b) ? a : b)),
    };
  }

  return bounds;
}

/**
 * Resolve the navigation period into absolute bounds anchored on the factory
 * production day. Pure function — every input is explicit, `now` included.
 *
 * @returns {{start, end, startFullDay, endFullDay, startWindow, endWindow,
 *   timezone, source}|null} naive-UTC strings, exclusive ends [start, end);
 *   null when unresolvable. `fullDay` = the period alone, `window` = period ×
 *   time range × custom hours (before the OF step), `start/end` = everything.
 */
export function resolveProductionPeriod({
  periodType,
  periodValue,
  factorySettings,
  selectedTimeRange = null,
  customHours = null,
  manufacturingOrderScope = 'TRUNCATED',
  manufacturingOrderBounds = null,
  tenantCreation = null,
  degradedMode = false,
  // Tenant default factory (S-0024): reference of the multi-factory scopes.
  defaultFactorySettings = null,
  now,
}) {
  const settings = resolveEffectiveSettings(
    factorySettings,
    { degradedMode, defaultFactorySettings },
  );
  const base = periodBounds(periodType, periodValue, settings);
  if (!base) {
    return null;
  }

  let { start, end } = base;

  if (customHours?.enabled) {
    ({ start, end } = applyCustomHours({ start, end }, customHours, settings));
  } else {
    ({ start, end } = applyTimeRange(
      { start, end },
      selectedTimeRange,
      periodType,
      periodValue,
      settings,
    ));
  }

  // Full production-day window of the same selection, regardless of the
  // time-range/custom-hours narrowing (legacy `fullDay` variant contract).
  const fullDay = base;

  if (tenantCreation && ['Month', 'Year'].includes(periodType)) {
    const creation = dayjs.utc(tenantCreation);
    if (creation.isAfter(start)) {
      start = creation;
    }
  }

  // Window of the selection BEFORE the manufacturing-order step (period × time
  // range × custom hours). Published so the OF picker can offer only the orders
  // that ran inside it — filtering on the final start/end would loop (list →
  // selection → bounds → list).
  const selectionWindow = { start, end };

  // Manufacturing-order step — LAST narrowing of the funnel (ADR-NAV-005):
  // period × time range (shift) × OF. `TRUNCATED` (default, « STRICT » in
  // product wording) clamps the OF run window to the window already resolved,
  // so the shift keeps the upper hand; `FULL` publishes the OF's own window
  // instead, whatever the period shows.
  if (manufacturingOrderBounds) {
    const orderStart = manufacturingOrderBounds.start
      ? dayjs.utc(manufacturingOrderBounds.start)
      : null;
    const orderEnd = manufacturingOrderBounds.end
      ? dayjs.utc(manufacturingOrderBounds.end)
      : null;

    if (manufacturingOrderScope === 'FULL') {
      start = orderStart ?? start;
      // No end = OF still running: up to now, never into the future.
      end = orderEnd ?? dayjs.utc(now);
    } else {
      const clampedStart = orderStart?.isAfter(start) ? orderStart : start;
      const clampedEnd = orderEnd?.isBefore(end) ? orderEnd : end;
      // An OF that ran entirely outside the selected time range would invert
      // the window: keep the window rather than publish an empty one, every
      // downstream consumer computes rates over this duration.
      if (clampedEnd.isAfter(clampedStart)) {
        start = clampedStart;
        end = clampedEnd;
      }
    }
  }

  return {
    start: toUtcNaive(start),
    end: toUtcNaive(end),
    startFullDay: toUtcNaive(fullDay.start),
    endFullDay: toUtcNaive(fullDay.end),
    startWindow: toUtcNaive(selectionWindow.start),
    endWindow: toUtcNaive(selectionWindow.end),
    // Production-day LABEL of the window's first day: the start instant's factory
    // date shifted by the J/J-1 offset (with J-1 the instant falls on the eve of
    // the day it is labeled). Widgets that name days (weekly timeline rows) read
    // this instead of re-deriving a date from the instant.
    firstDayLabel: dayjs(fullDay.start.format('YYYY-MM-DD'))
      .add(settings.startProductionDayOffset, 'day')
      .format('YYYY-MM-DD'),
    timezone: settings.timezone,
    source: NAV_NEXT_SOURCE,
  };
}
